import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../firebase/firebase";

import {
  loadDriverPlanMap,
  getDriverPlanForDate,
} from "./driverPlanHistory";

export default function DriverPlanManager() {
  const [drivers, setDrivers] = useState([]);
  const [planMap, setPlanMap] = useState({});
  const [search, setSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [newPlanType, setNewPlanType] = useState("FIXED");
  const [effectiveDate, setEffectiveDate] = useState("");

  /* -------------------------------------------------------
      LOAD DRIVERS + PLAN HISTORY
  ------------------------------------------------------- */
  useEffect(() => {
    async function loadData() {
      setLoading(true);

      const snap = await getDocs(collection(db, "drivers"));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => a.fullName.localeCompare(b.fullName)); // A-Z sort
      setDrivers(list);

      const map = await loadDriverPlanMap();
      setPlanMap(map);

      setLoading(false);
    }
    loadData();
  }, []);

  /* -------------------------------------------------------
      FILTERING
  ------------------------------------------------------- */
  const filtered = drivers.filter((d) => {
    const q = search.toLowerCase();
    const matchesSearch =
      d.fullName?.toLowerCase().includes(q) ||
      d.company?.toLowerCase().includes(q);

    const matchesCompany =
      companyFilter === "ALL" || d.company === companyFilter;

    return matchesSearch && matchesCompany;
  });

  /* -------------------------------------------------------
      OPEN MODAL
  ------------------------------------------------------- */
  function openChangePlan(driver) {
    setSelectedDriver(driver);
    setNewPlanType("FIXED");
    setEffectiveDate("");
    setModalOpen(true);
  }

  /* -------------------------------------------------------
      SAVE NEW PLAN
  ------------------------------------------------------- */
  async function applyPlanChange() {
    if (!selectedDriver) return;
    if (!effectiveDate) {
      alert("Please select an effective date.");
      return;
    }

    const driverId = selectedDriver.id;
    const newStart = new Date(effectiveDate);
    newStart.setHours(0, 0, 0, 0);

    const history = planMap[driverId] || [];
    const last = history[history.length - 1];

    if (last) {
      const endDate = new Date(newStart);
      endDate.setDate(endDate.getDate() - 1);

      if (!last.endDate || last.endDate > endDate) {
        if (last._docRef) {
          await updateDoc(last._docRef, {
            endDate: Timestamp.fromDate(endDate),
          });
        }
      }
    }

    // Create new period
    await addDoc(collection(db, "driverPlanHistory"), {
      driverId,
      driverName: selectedDriver.fullName || "",
      planType: newPlanType,
      startDate: Timestamp.fromDate(newStart),
      endDate: null,
      company: selectedDriver.company || "",
      createdAt: Timestamp.now(),
    });

    alert("Driver plan updated.");
    setModalOpen(false);

    // Reload plan history
    const updated = await loadDriverPlanMap();
    setPlanMap(updated);
  }

  /* -------------------------------------------------------
      RENDER
  ------------------------------------------------------- */
  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ marginBottom: 10 }}>Driver Plan Manager</h2>
      <p style={{ marginBottom: 20, color: "#555" }}>
        Manage Fixed Base and Salary & Commission plans for all drivers.
      </p>

      {/* FILTER BAR */}
      <div style={{ display: "flex", gap: 15, marginBottom: 15 }}>
        <input
          type="text"
          placeholder="Search driver or company..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: 8,
            width: 260,
            borderRadius: 6,
            border: "1px solid #ccc",
          }}
        />

        <select
          value={companyFilter}
          onChange={(e) => setCompanyFilter(e.target.value)}
          style={{
            padding: 8,
            width: 260,
            borderRadius: 6,
            border: "1px solid #ccc",
          }}
        >
          <option value="ALL">All Companies</option>
          <option value="Lolia Limousine for Luxury Motor Vehicles Services LLC">
            Lolia
          </option>
          <option value="Comfort Line Luxury Motor Vehicles Services LLC">
            Comfort Line
          </option>
        </select>
      </div>

      {loading ? (
        <p>Loading drivers...</p>
      ) : (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 13,
          }}
        >
          <thead>
            <tr>
              <th style={th}>Driver Name</th>
              <th style={th}>Company</th>
              <th style={th}>Current Plan</th>
              <th style={th}>Action</th>
            </tr>
          </thead>

          <tbody>
            {filtered.map((d) => {
              const plan = getDriverPlanForDate({
                driverId: d.id,
                date: new Date(),
                planMap,
              });

              return (
                <tr key={d.id}>
                  <td style={td}>{d.fullName}</td>
                  <td style={td}>{d.company}</td>
                  <td style={td}>
                    {plan === "FIXED" ? (
                      <span style={badgeFixed}>FIXED</span>
                    ) : (
                      <span style={badgeSalary}>SALARY</span>
                    )}
                  </td>

                  <td style={td}>
                    <button onClick={() => openChangePlan(d)} style={btn}>
                      Update Plan
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* MODAL */}
      {modalOpen && (
        <div style={modalOverlay}>
          <div style={modalBox}>
            <h3>Change Plan – {selectedDriver?.fullName}</h3>

            <label style={label}>Select Plan</label>
            <select
              value={newPlanType}
              onChange={(e) => setNewPlanType(e.target.value)}
              style={input}
            >
              <option value="FIXED">Fixed Base Driver</option>
              <option value="SALARY">Salary & Commission</option>
            </select>

            <label style={label}>Effective From</label>
            <input
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              style={input}
            />

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={() => setModalOpen(false)} style={btnCancel}>
                Cancel
              </button>
              <button onClick={applyPlanChange} style={btnSave}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------- STYLES ---------------------- */

const th = {
  padding: "8px 6px",
  background: "#f0f3f7",
  borderBottom: "1px solid #ddd",
  textAlign: "left",
};

const td = {
  padding: "8px 6px",
  borderBottom: "1px solid #eee",
};

const btn = {
  padding: "4px 10px",
  fontSize: 12,
  borderRadius: 5,
  cursor: "pointer",
  background: "#0d47a1",
  color: "white",
  border: "none",
};

const badgeFixed = {
  padding: "3px 8px",
  background: "#e3f2fd",
  color: "#0d47a1",
  borderRadius: 4,
  fontWeight: 600,
};

const badgeSalary = {
  padding: "3px 8px",
  background: "#ffebee",
  color: "#c62828",
  borderRadius: 4,
  fontWeight: 600,
};

const modalOverlay = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 9999,
};

const modalBox = {
  background: "white",
  width: 400,
  padding: 20,
  borderRadius: 10,
};

const label = { fontSize: 12, marginTop: 12, display: "block" };

const input = {
  width: "100%",
  padding: 6,
  marginTop: 4,
  borderRadius: 5,
  border: "1px solid #ccc",
};

const btnCancel = {
  padding: "6px 12px",
  background: "#eee",
  borderRadius: 5,
  cursor: "pointer",
  fontSize: 12,
};

const btnSave = {
  padding: "6px 12px",
  background: "#0d47a1",
  color: "#fff",
  borderRadius: 5,
  cursor: "pointer",
  fontSize: 12,
};
